import { ageOn, birthdayOf, detectDateOrder, byAge, displayName, distances, generations, lifespan, outline, type Graph, type OutlineNode, type Person } from '@lineage/core';

import { Fragment, useMemo, useState } from 'react';
import { Splash } from '../../components/Guard';
import { useTree } from '../../lib/queries';
import { useTreeModel } from '../canvas/model';
import { PersonRow, useOpenPerson } from './PersonRow';
import { compareNames, fold, useLang, useT } from '../../i18n';
import { BackLink } from '../../components/BackLink';

const VIEWS = ['folders', 'age', 'distance', 'generation', 'name', 'place'] as const;
type ViewId = (typeof VIEWS)[number];

const KEY = 'lineage.listView';
const readView = (): ViewId => {
  try {
    const v = localStorage.getItem(KEY);
    if (VIEWS.some((x) => x === v)) return v as ViewId;
  } catch {
    /* ignore */
  }
  return 'folders';
};

export function ViewsPage({ treeId }: { treeId: string }) {
  const t = useT();
  const tv = t.views;
  const lang = useLang();
  const cmp = compareNames(lang);
  const q = useTree(treeId);
  const snap = q.data;
  const meId = snap ? snap.me.perspectivePersonId ?? snap.me.personId : null;
  const model = useTreeModel(snap ?? { people: [], parentLinks: [], partnerships: [], version: 0 }, meId);
  const open = useOpenPerson(treeId);
  const [view, setViewState] = useState<ViewId>(readView);
  const [oldestFirst, setOldestFirst] = useState(true);
  const [search, setSearch] = useState('');
  const setView = (v: ViewId) => {
    setViewState(v);
    try {
      localStorage.setItem(KEY, v);
    } catch {
      /* ignore */
    }
  };
  if (q.isLoading) return <Splash />;
  if (!snap) return <div className="center"><p className="muted">{t.common.couldntLoadTree}</p></div>;

  const g = model.graph;
  const needle = fold(search.trim());
  const matches = (p: Person) => !needle || fold(displayName(p)).includes(needle);
  const bornIn = (y: number) => lifespan({ bornYear: y, diedYear: null, living: true }, lang);
  const today = new Date();
  const order = detectDateOrder(snap.people.map((p) => p.born));
  const ageText = (p: Person) => {
    const b = birthdayOf(p, order);
    const age = b ? ageOn(b, today) : p.bornYear ? today.getFullYear() - p.bornYear : null;
    if (age === null || (!p.living && !p.diedYear)) return p.bornYear ? bornIn(p.bornYear) : tv.birthUnknown;
    if (!p.living) return `${p.bornYear ?? '?'} – ${p.diedYear}`;
    return tv.age(age, bornIn(p.bornYear!));
  };
  const row = (p: Person, sub?: string, aside?: string) => <PersonRow key={p.id} person={p} sub={sub ?? ageText(p)} aside={aside} onOpen={open} />;
  const hint = tv.modes[view].hint;

  return (
    <div className="page">
      <div className="row" style={{ marginBottom: 16 }}>
        <BackLink fallback={{ to: '/t/$treeId', params: { treeId } }} aria-label={t.common.backToTree}>←</BackLink>
        <h1 className="grow">{tv.asList(snap.tree.name)}</h1>
      </div>
      <div className="stack">
        <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
          <div className="segmented" role="group" aria-label={tv.view}>
            {VIEWS.map((v) => (
              <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)} style={{ whiteSpace: 'nowrap' }}>{tv.modes[v].label}</button>
            ))}
          </div>
        </div>
        <p className="muted" style={{ margin: 0 }}>{hint}</p>
        <div className="row">
          <input className="input grow" type="search" placeholder={t.common.searchByName} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t.common.searchPeople} />
          {view === 'age' && <button className="btn" type="button" onClick={() => setOldestFirst((o) => !o)}>{oldestFirst ? tv.oldestFirst : tv.youngestFirst}</button>}
        </div>
        {snap.people.length === 0 && <p className="muted">{tv.emptyTree}</p>}

        {view === 'folders' && <Folders nodes={outline(g)} g={g} open={open} needle={needle} sub={ageText} defaultOpen={snap.people.length <= 60} />}

        {view === 'age' && (() => {
          const ids = byAge(g, snap.people.filter(matches).map((p) => p.id));
          const known = ids.filter((id) => g.people.get(id)!.bornYear !== null);
          const unknown = ids.filter((id) => g.people.get(id)!.bornYear === null);
          const ordered = oldestFirst ? known : [...known].reverse();
          return (
            <>
              {ordered.map((id, i) => row(g.people.get(id)!, undefined, `#${i + 1}`))}
              {unknown.length > 0 && <h3 className="section-title">{tv.birthUnknown}</h3>}
              {unknown.map((id) => row(g.people.get(id)!, tv.addBirth))}
            </>
          );
        })()}

        {view === 'distance' && (() => {
          if (!meId) return <p className="muted">{tv.pickYourself}</p>;
          const d = distances(g, meId);
          const list = snap.people.filter(matches).filter((p) => d.has(p.id)).sort((a, b) => d.get(a.id)! - d.get(b.id)! || g.order.indexOf(a.id) - g.order.indexOf(b.id));
          const far = snap.people.filter(matches).filter((p) => !d.has(p.id));
          let last = -1;
          return (
            <>
              {list.map((p) => {
                const n = d.get(p.id)!;
                const head = n !== last ? <h3 key={`h${n}`} className="section-title">{n === 0 ? model.pack.label(model.kins.get(meId)!) : tv.steps(n)}</h3> : null;
                last = n;
                return <Fragment key={p.id}>{head}{row(p, model.labels.get(p.id) || undefined, model.labels.get(p.id) ? ageText(p) : undefined)}</Fragment>;
              })}
              {far.length > 0 && <h3 className="section-title">{tv.notConnected}</h3>}
              {far.map((p) => row(p))}
            </>
          );
        })()}

        {view === 'generation' && (() => {
          const gen = generations(g);
          const groups = new Map<number, string[]>();
          for (const id of byAge(g, snap.people.filter(matches).map((p) => p.id))) groups.set(gen.get(id)!, [...(groups.get(gen.get(id)!) ?? []), id]);
          return [...groups.entries()].sort((a, b) => a[0] - b[0]).map(([n, ids]) => (
            <section key={n} className="stack" style={{ gap: 8 }}>
              <h3 className="section-title">{tv.generation(n + 1, ids.length)}</h3>
              {ids.map((id) => row(g.people.get(id)!, model.labels.get(id) ? `${model.labels.get(id)} · ${ageText(g.people.get(id)!)}` : undefined))}
            </section>
          ));
        })()}

        {view === 'name' && [...snap.people].filter(matches)
          .sort((a, b) => cmp(a.lastName ?? a.firstName ?? '', b.lastName ?? b.firstName ?? '') || cmp(a.firstName ?? '', b.firstName ?? ''))
          .map((p) => row(p, model.labels.get(p.id) ? `${model.labels.get(p.id)} · ${ageText(p)}` : undefined))}

        {view === 'place' && (() => {
          const groups = new Map<string, Person[]>();
          for (const p of snap.people.filter(matches)) {
            const k = p.residence?.trim() || '';
            groups.set(k, [...(groups.get(k) ?? []), p]);
          }
          return [...groups.entries()]
            .sort((a, b) => (a[0] === '' ? 1 : b[0] === '' ? -1 : cmp(a[0], b[0])))
            .map(([place, ps]) => (
              <section key={place} className="stack" style={{ gap: 8 }}>
                <h3 className="section-title">{place || tv.noPlace} · {ps.length}</h3>
                {ps.map((p) => row(p))}
              </section>
            ));
        })()}
      </div>
    </div>
  );
}

function Folders({ nodes, g, open, needle, sub, defaultOpen }: { nodes: OutlineNode[]; g: Graph; open: (id: string) => void; needle: string; sub: (p: Person) => string; defaultOpen: boolean }) {
  const t = useT();
  // While searching, show every branch that contains a match.
  const hit = useMemo(() => {
    const ok = new Set<string>();
    if (!needle) return ok;
    const walk = (n: OutlineNode): boolean => {
      const names = [n.id, ...n.partners].some((id) => fold(displayName(g.people.get(id)!)).includes(needle));
      const kids = n.children.map(walk).some(Boolean);
      if (names || kids) ok.add(n.id);
      return names || kids;
    };
    nodes.forEach(walk);
    return ok;
  }, [nodes, g, needle]);
  const shown = needle ? nodes.filter((n) => hit.has(n.id)) : nodes;
  if (needle && !shown.length) return <p className="muted">{t.common.noMatch(needle)}</p>;
  return <div className="stack" style={{ gap: 8 }}>{shown.map((n) => <Branch key={n.id} node={n} g={g} open={open} sub={sub} hit={needle ? hit : null} startOpen={defaultOpen || Boolean(needle)} />)}</div>;
}

function Branch({ node, g, open, sub, hit, startOpen }: { node: OutlineNode; g: Graph; open: (id: string) => void; sub: (p: Person) => string; hit: Set<string> | null; startOpen: boolean }) {
  const tv = useT().views;
  const [expanded, setExpanded] = useState(startOpen);
  const kids = hit ? node.children.filter((c) => hit.has(c.id)) : node.children;
  const person = g.people.get(node.id)!;
  const isOpen = expanded || Boolean(hit);
  return (
    <div>
      <div className="row" style={{ gap: 4, alignItems: 'stretch' }}>
        {node.children.length > 0 ? (
          <button type="button" className="btn btn-ghost" aria-expanded={isOpen} aria-label={tv.toggleChildren(isOpen, displayName(person))} onClick={() => setExpanded((e) => !e)} style={{ minWidth: 44 }}>{isOpen ? '▾' : '▸'}</button>
        ) : (
          <span style={{ minWidth: 44 }} aria-hidden="true" />
        )}
        <div className="grow stack" style={{ gap: 4, minWidth: 0 }}>
          <PersonRow person={person} sub={sub(person)} aside={node.children.length ? tv.below(countDescendants(node)) : undefined} onOpen={open} />
          {node.partners.map((id) => (
            <PersonRow key={id} person={g.people.get(id)!} sub={tv.partner(sub(g.people.get(id)!))} onOpen={open} />
          ))}
        </div>
      </div>
      {isOpen && kids.length > 0 && (
        <div className="stack" style={{ gap: 8, marginTop: 8, marginLeft: 22, paddingLeft: 12, borderLeft: '1px solid var(--line-strong)' }}>
          {kids.map((c) => <Branch key={c.id} node={c} g={g} open={open} sub={sub} hit={hit} startOpen={startOpen} />)}
        </div>
      )}
    </div>
  );
}

const countDescendants = (n: OutlineNode): number => n.children.reduce((s, c) => s + 1 + c.partners.length + countDescendants(c), 0);
